import { hexToRgb } from 'assets/jss/material-dashboard-pro-react';
import styles from 'assets/jss/material-dashboard-pro-react/components/campaignStepsStyle';
import cx from 'classnames';
import moment from 'moment';
import React from 'react';
import { toast } from 'sonner';
import { ECampaignStatus } from 'types/campaign';
import { EVoicemailTouchBehavior } from 'types/touch-trigger';
import { ETouchType } from 'types/touch-type';

import { ButtonBase, Tooltip } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import DeleteOutlineRoundedIcon from '@material-ui/icons/DeleteOutlineRounded';
import EditRoundedIcon from '@material-ui/icons/EditRounded';
import EmailIcon from '@material-ui/icons/Email';
import UpdateIcon from '@material-ui/icons/Update';
import VisibilityRoundedIcon from '@material-ui/icons/VisibilityRounded';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import {
  Timeline, TimelineConnector, TimelineContent, TimelineDot,
  TimelineItem, TimelineSeparator,
} from '@material-ui/lab';

import Button from 'creative-components/CustomButtons/Button';

import ActionModal from 'components/ActionModal/ActionModal';
import { useAuthDataContext } from 'components/AuthDataProvider/AuthDataProvider';
import AddExtraTouchModal from 'components/CampaignSteps/CustomTouches/AddExtraTouchModal';
import DelayTouchModal from 'components/CampaignSteps/CustomTouches/DelayTouchModal';
import EditCustomTouchTemplateModal from 'components/CampaignSteps/CustomTouches/EditCustomTouchTemplateModal';
import RescheduleExtraTouchModal from 'components/CampaignSteps/CustomTouches/RescheduleExtraTouchModal';
import { getDefaultExtraTouchSendAt } from 'components/CampaignSteps/CustomTouches/utils';
import ExtraTouchOnboarding from 'components/CampaignSteps/ExtraTouchOnboarding';
import PreviewEmailModal from 'components/CampaignSteps/PreviewEmailModal';
import PreviewPostcardLetterModal from 'components/CampaignSteps/PreviewPostcardLetterModal';
import DashboardBubble from 'components/DashboardBubble/DashboardBubble';
import { useDataFilterContext } from 'components/DataMap/DataFilterProvider';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import { useLoadingIndicatorContext } from 'components/LoadingIndicator/LoadingIndicatorProvider';

import {
  deleteCampaignExtraTouch, getTouchPreview, getVoicemails,
} from 'utils/api';
import {
  enumToReadableName,
  getCampaignTouchDisplaySendAt,
  getColorForTouchType,
  getHumanProcessingNotSentReason,
  getIconForTouchType,
  getTouchDisplayName,
  isCampaignUsingCalendarBasedSystem,
  isCrmProEventType,
  isExtraCampaignTouch,
  isIntroEmailTouch,
  showAPIErrorAlert,
  sortCampaignTouchesForSteps,
} from 'utils/lib';

const useStyles = makeStyles(styles);

const touchNeedsCustomization = (touchTrigger) => (
  touchTrigger.requiresCustomTouchTemplate
  && !touchTrigger.customTouchTemplate
  && !touchTrigger.processed
  && !touchTrigger.processingNotSentReason
);

const hasVoicemailFallbackEmail = (touchTrigger) => (
  touchTrigger.touchType?.name === ETouchType.Voicemail
  && !!touchTrigger.metadata?.subject
  && touchTrigger.metadata?.voicemailTouchBehavior !== EVoicemailTouchBehavior.AllLeads
);

const CampaignSteps = ({
  agent,
  eventType,
  initialTouchTriggers,
  campaignStartDate,
  campaignEndDate = null,
  campaignId = null,
  campaignStatus,
  extraTouchPricePerApnCents = null,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { isAdmin, isAnyAgent } = useAuthDataContext();
  const { availableEventTypes } = useDataFilterContext();
  const { showLoadingIndicatorModal, hideLoadingIndicatorModal } = useLoadingIndicatorContext();

  const sortTouchesForCampaign = React.useCallback(
    (nextTouchTriggers) => sortCampaignTouchesForSteps(nextTouchTriggers, campaignStatus, eventType),
    [campaignStatus, eventType],
  );

  const [touchTriggers, setTouchTriggers] = React.useState(() => sortTouchesForCampaign(initialTouchTriggers));
  const [touchCards, setTouchCards] = React.useState(null);
  const previousCampaignStatus = React.useRef(campaignStatus);

  React.useEffect(() => {
    if (previousCampaignStatus.current === campaignStatus) return;

    previousCampaignStatus.current = campaignStatus;
    setTouchTriggers(sortTouchesForCampaign(initialTouchTriggers));
  }, [campaignStatus, initialTouchTriggers, sortTouchesForCampaign]);

  const [isAddingExtraTouch, setIsAddingExtraTouch] = React.useState(false);
  const [defaultExtraTouchSendAt, setDefaultExtraTouchSendAt] = React.useState(null);
  const [isDelayingId, setIsDelayingId] = React.useState(null);
  const [isReschedulingId, setIsReschedulingId] = React.useState(null);
  const [isDeletingId, setIsDeletingId] = React.useState(null);
  const [isEditingId, setIsEditingId] = React.useState(null);
  const [isPreviewingId, setIsPreviewingId] = React.useState(null);

  const [eventTypeVoicemails, setEventTypeVoicemails] = React.useState(null);

  const canManageExtraTouches = isAnyAgent && campaignId && campaignStatus === ECampaignStatus.Active;

  const supportedExtraTouchTypes = React.useMemo(() => {
    const supportedTouchTypeNames = [ETouchType.Email, ETouchType.Postcard, ETouchType.Letter];

    return touchTriggers
      .filter((touchTrigger) => supportedTouchTypeNames.includes(touchTrigger.touchType?.name) && !!touchTrigger.touchType?._id)
      .reduce((accumulator, touchTrigger) => {
        if (accumulator.some((existing) => existing.touchTypeName === touchTrigger.touchType.name)) return accumulator;

        accumulator.push({
          touchTypeId: touchTrigger.touchType._id,
          touchTypeName: touchTrigger.touchType.name,
        });
        return accumulator;
      }, []);
  }, [touchTriggers]);

  const standardTouchNumberById = React.useMemo(() => {
    let touchNumber = 0;

    return touchTriggers.reduce((accumulator, touchTrigger) => {
      if (!isExtraCampaignTouch(touchTrigger) && !isIntroEmailTouch(touchTrigger)) {
        touchNumber += 1;
        accumulator.set(touchTrigger._id, touchNumber);
      }

      return accumulator;
    }, new Map());
  }, [touchTriggers]);

  const upsertTouchTrigger = React.useCallback((editedTouchTrigger) => {
    setTouchTriggers((currentTouchTriggers) => {
      const nextTouchTriggers = currentTouchTriggers.some(({ _id }) => _id === editedTouchTrigger._id)
        ? currentTouchTriggers.map((touchTrigger) => (touchTrigger._id === editedTouchTrigger._id ? editedTouchTrigger : touchTrigger))
        : [...currentTouchTriggers, editedTouchTrigger];

      return sortTouchesForCampaign(nextTouchTriggers);
    });
  }, [sortTouchesForCampaign]);

  const removeTouchTrigger = React.useCallback((deletedTouchTriggerId) => {
    setTouchTriggers((currentTouchTriggers) => currentTouchTriggers.filter(({ _id }) => _id !== deletedTouchTriggerId));
  }, []);

  const openAddExtraTouchModal = React.useCallback((sendAt = null) => {
    setDefaultExtraTouchSendAt(sendAt);
    setIsAddingExtraTouch(true);
  }, []);

  const handlePreviewTouch = React.useCallback((touchTrigger) => {
    if (touchNeedsCustomization(touchTrigger)) {
      toast.error('This touch needs to be customized before it can be previewed.');
      return;
    }

    setIsPreviewingId(touchTrigger._id);
  }, []);

  React.useEffect(() => {
    (async () => {
      if (!availableEventTypes) return;

      try {
        const { voicemails } = await getVoicemails();

        setEventTypeVoicemails(voicemails);
      } catch (err) {
        console.error(err);
        showAPIErrorAlert(err);
      }
    })();
  }, [availableEventTypes]);

  React.useEffect(() => {
    (async () => {
      if (!agent || !eventType || !touchTriggers) return;

      let nextTouchToGoOutFound = false;

      setTouchCards(touchTriggers.map((touchTrigger) => {
        const {
          description, processAt, sendAt, processingNotSentReason, touchType: { name: touchTypeName }, customTouchTemplate, metadata,
          requiresCustomTouchTemplate,
        } = touchTrigger;

        let { processed } = touchTrigger;

        // Special handling for CRM Pro birthday touch triggers
        if (isCrmProEventType(eventType) && description.toLowerCase().includes('birthday')) {
          processed = false;
        }

        const nextTouchToGoOut = !nextTouchToGoOutFound && !processed;
        if (nextTouchToGoOut) nextTouchToGoOutFound = true;

        let leftContent = null;
        let rightContent = null;
        const needsCustomization = touchNeedsCustomization(touchTrigger);

        switch (touchTypeName) {
          case ETouchType.Voicemail: {
            const { voicemailTouchBehavior, voicemail, subject } = metadata;

            // Only display VM recording info if the behavior selected has a VM going out
            if (voicemailTouchBehavior !== EVoicemailTouchBehavior.None) {
              if (voicemail) {
                leftContent = (
                  <>
                    <h4>{`Recording Assigned: ${voicemail.name}`}</h4>
                    {voicemail.archived && <h5>This recording is archived, but it will still be used for this touch unless replaced.</h5>}
                  </>
                );

                rightContent = (
                  <audio
                    src={voicemail.recordingUrl}
                    type="audio/mpeg"
                    controls
                    preload="auto"
                  />
                );
              } else if (!touchTrigger.processed) {
                leftContent = (
                  <DashboardBubble
                    rootClass={classes.campaignDatesMessage}
                    backgroundColor={`rgb(${hexToRgb(theme.palette.warning.main)}, 0.1)`}
                    icon={<WarningRoundedIcon htmlColor={theme.palette.warning.main} />}
                    content="You don't have an audio recording assigned to this voicemail touch. Click the edit button to assign or record one."
                  />
                );
              } else {
                leftContent = (
                  <DashboardBubble
                    rootClass={classes.campaignDatesMessage}
                    backgroundColor={`rgb(${hexToRgb(theme.palette.warning.main)}, 0.1)`}
                    icon={<WarningRoundedIcon htmlColor={theme.palette.warning.main} />}
                    content="There was no voicemail recording assigned to this touch."
                  />
                );
              }
            }

            // Only display email info if the behavior selceted has an email going out
            if (voicemailTouchBehavior !== EVoicemailTouchBehavior.AllLeads) {
              leftContent = (
                <>
                  <h4 style={{ marginBottom: '16px' }}>{`Email Subject: ${customTouchTemplate?.extraMergeVariables.customEmailSubjectWithMergeVariables ?? subject}`}</h4>
                  {leftContent}
                </>
              );
            }

            break;
          }
          case ETouchType.Email: {
            const { subject } = metadata;
            leftContent = <h4>{customTouchTemplate?.extraMergeVariables.customEmailSubjectWithMergeVariables ?? subject}</h4>;
            break;
          }
          case ETouchType.Letter: {
            leftContent = <div />;
            break;
          }
          case ETouchType.Postcard: {
            leftContent = <div />;
            break;
          }
          case ETouchType.Text: {
            break;
          }
          default: {
            break;
          }
        }

        if (needsCustomization) {
          leftContent = (
            <>
              {leftContent}
              <div style={{ marginTop: '16px' }}>
                <DashboardBubble
                  rootClass={classes.campaignDatesMessage}
                  backgroundColor={`rgb(${hexToRgb(theme.palette.warning.main)}, 0.1)`}
                  icon={<WarningRoundedIcon htmlColor={theme.palette.warning.main} />}
                  content="Needs customization before it can be sent."
                />
              </div>
            </>
          );
        }

        return {
          touchTrigger,
          nextTouchToGoOut,
          processed,
          touchTypeName,
          leftContent,
          rightContent,
          sendAt,
          processAt,
          processingNotSentReason,
        };
      }));
    })();
  }, [agent, eventType, touchTriggers, classes.campaignDatesMessage, theme.palette.warning.main]);

  const sendEmailPreview = async (touchTrigger) => {
    showLoadingIndicatorModal();

    const toastId = toast.loading('Generating preview...');

    try {
      await getTouchPreview(touchTrigger._id, { sendEmailPreview: true });

      toast.success('You should receive a sample email in your mailbox shortly.', { id: toastId });
    } catch (err) {
      console.error(err);
      showAPIErrorAlert(err);
    }

    hideLoadingIndicatorModal();
  };

  if (!availableEventTypes || !eventTypeVoicemails || !touchCards) return <LoadingIndicator />;

  const touchTriggerPendingDelete = isDeletingId ? touchTriggers.find((o) => o._id === isDeletingId) : null;

  let activeModal = null;
  if (isAddingExtraTouch) {
    activeModal = (
      <AddExtraTouchModal
        campaignId={campaignId}
        touchTypeOptions={supportedExtraTouchTypes}
        defaultSendAt={defaultExtraTouchSendAt}
        campaignEndDate={campaignEndDate}
        extraTouchPricePerApnCents={extraTouchPricePerApnCents}
        onClose={() => {
          setIsAddingExtraTouch(false);
          setDefaultExtraTouchSendAt(null);
        }}
        onTouchCreated={(touchTrigger) => {
          upsertTouchTrigger(touchTrigger);
          setIsEditingId(touchTrigger._id);
        }}
      />
    );
  } else if (isEditingId || isPreviewingId) {
    let touchTrigger;
    if (isEditingId) {
      touchTrigger = touchTriggers.find((o) => o._id === isEditingId);
    } else {
      touchTrigger = touchTriggers.find((o) => o._id === isPreviewingId);
    }

    const { touchType: { name: touchTypeName } } = touchTrigger;

    switch (touchTypeName) {
      case ETouchType.Voicemail: {
        // NOTE: We handle voicemail fallback email previews in a useEffect hook above

        if (isEditingId) {
          activeModal = (
            <EditCustomTouchTemplateModal
              eventTypeVoicemails={eventTypeVoicemails}
              touchTrigger={touchTrigger}
              campaignTouchTriggers={touchTriggers}
              onReceiveEditedTouchTrigger={upsertTouchTrigger}
              onClose={() => setIsEditingId(null)}
            />
          );
        } else if (isPreviewingId) {
          activeModal = (
            <PreviewEmailModal
              onClose={() => setIsPreviewingId(null)}
              touchTrigger={touchTrigger}
            />
          );
        }

        break;
      }
      case ETouchType.Email: {
        // NOTE: We handle email previews in a useEffect hook above

        if (isEditingId) {
          activeModal = (
            <EditCustomTouchTemplateModal
              touchTrigger={touchTrigger}
              campaignTouchTriggers={touchTriggers}
              onReceiveEditedTouchTrigger={upsertTouchTrigger}
              onClose={() => setIsEditingId(null)}
            />
          );
        } else if (isPreviewingId) {
          activeModal = (
            <PreviewEmailModal
              onClose={() => setIsPreviewingId(null)}
              touchTrigger={touchTrigger}
            />
          );
        }

        break;
      }
      case ETouchType.Letter: {
        if (isEditingId) {
          activeModal = (
            <EditCustomTouchTemplateModal
              touchTrigger={touchTrigger}
              campaignTouchTriggers={touchTriggers}
              onReceiveEditedTouchTrigger={upsertTouchTrigger}
              onClose={() => setIsEditingId(null)}
            />
          );
        } else if (isPreviewingId) {
          activeModal = (
            <PreviewPostcardLetterModal
              onClose={() => setIsPreviewingId(null)}
              touchTrigger={touchTrigger}
            />
          );
        }

        break;
      }
      case ETouchType.Postcard: {
        if (isEditingId) {
          activeModal = (
            <EditCustomTouchTemplateModal
              touchTrigger={touchTrigger}
              campaignTouchTriggers={touchTriggers}
              onReceiveEditedTouchTrigger={upsertTouchTrigger}
              onClose={() => setIsEditingId(null)}
            />
          );
        } else if (isPreviewingId) {
          activeModal = (
            <PreviewPostcardLetterModal
              onClose={() => setIsPreviewingId(null)}
              touchTrigger={touchTrigger}
            />
          );
        }

        break;
      }
      case ETouchType.Text: {
        break;
      }
      default: {
        break;
      }
    }
  } else if (isReschedulingId) {
    const touchTrigger = touchTriggers.find((o) => o._id === isReschedulingId);

    activeModal = (
      <RescheduleExtraTouchModal
        onClose={() => setIsReschedulingId(null)}
        touchTrigger={touchTrigger}
        campaignEndDate={campaignEndDate}
        onReceiveEditedTouchTrigger={upsertTouchTrigger}
      />
    );
  } else if (isDelayingId) {
    const touchTrigger = touchTriggers.find((o) => o._id === isDelayingId);

    activeModal = (
      <DelayTouchModal
        onClose={() => setIsDelayingId(null)}
        touchTrigger={touchTrigger}
        onReceiveEditedTouchTrigger={upsertTouchTrigger}
      />
    );
  }

  return (
    <>
      {touchTriggerPendingDelete && (
        <ActionModal
          backgroundColor="#ffffff"
          textColor="#1f2937"
          onClose={() => setIsDeletingId(null)}
          title="Delete Extra Touch"
          message="Are you sure you want to delete this extra touch? This cannot be undone."
          buttons={[
            <ButtonBase onClick={() => setIsDeletingId(null)} className={classes.cancelButton}>Cancel</ButtonBase>,
            <ButtonBase
              onClick={async () => {
                showLoadingIndicatorModal();
                try {
                  const { deletedTouchTriggerId } = await deleteCampaignExtraTouch(touchTriggerPendingDelete._id);
                  removeTouchTrigger(deletedTouchTriggerId);
                  setIsDeletingId(null);
                  toast.success('Extra touch deleted.');
                } catch (err) {
                  console.error(err);
                  showAPIErrorAlert(err);
                } finally {
                  hideLoadingIndicatorModal();
                }
              }}
            >
              Delete
            </ButtonBase>,
          ]}
        />
      )}

      {activeModal}

      <ExtraTouchOnboarding
        isEnabled={canManageExtraTouches}
        cancelButtonClassName={classes.cancelButton}
        onAddOneNow={() => openAddExtraTouchModal()}
      />

      {canManageExtraTouches && (
        <div className={classes.extraTouchActionsBar}>
          <Button
            data-tour-id="extra-touch-action"
            color="primary"
            round
            onClick={() => openAddExtraTouchModal()}
            disabled={supportedExtraTouchTypes.length === 0}
          >
            <AddCircleOutlineIcon />
            Add Extra Touch
          </Button>
        </div>
      )}

      <Timeline className={classes.timeline}>
        {touchCards.map((touchCard, i) => {
          const {
            touchTrigger, nextTouchToGoOut, processed, processingNotSentReason, touchTypeName, leftContent, rightContent, sendAt, processAt,
          } = touchCard;
          const nextTouchCard = touchCards[i + 1];
          const isExtraTouch = isExtraCampaignTouch(touchTrigger);
          const now = moment();
          const showInlineInsertion = canManageExtraTouches
            && (
              nextTouchCard
                ? moment(nextTouchCard.touchTrigger.sendAt).isAfter(now)
                : moment(touchTrigger.sendAt).isAfter(now)
            );

          const Icon = getIconForTouchType(touchTypeName);

          const isMailTouch = touchTypeName === 'POSTCARD' || touchTypeName === 'LETTER';
          const touchNumber = standardTouchNumberById.get(touchTrigger._id);
          const displaySendAt = getCampaignTouchDisplaySendAt(touchTrigger, campaignStatus);

          let touchTitle;

          if (isExtraTouch) {
            touchTitle = (
              <>
                <span className={classes.extraTouchBadge}>Extra</span>
                {`${enumToReadableName(touchTrigger.touchType.name)} - ${moment(sendAt).format('MMMM Do, YYYY [at] h:mm A')}`}
              </>
            );
          } else if (isIntroEmailTouch(touchTrigger)) {
            touchTitle = `Introductory Email Touch - ${displaySendAt.format(campaignStartDate ? 'MMMM Do, YYYY' : 'MMMM Do')}`;
          } else if (campaignStartDate) {
            touchTitle = `Touch ${touchNumber} - ${moment(sendAt).format('MMMM Do, YYYY')}`;
          } else if (isCampaignUsingCalendarBasedSystem(eventType.name)) {
            touchTitle = `Touch ${touchNumber} - ${moment(sendAt).format('MMMM Do')}`;
          } else {
            touchTitle = `Touch ${touchNumber}`;
          }

          return (
            <TimelineItem key={i}>
              <TimelineSeparator>
                <TimelineDot>
                  <div className={cx(nextTouchToGoOut && classes.timelineIconContainerBlink)}>
                    <div
                      className={classes.timelineIconContainer}
                      style={{
                        backgroundColor: !processingNotSentReason && (processed || nextTouchToGoOut) ? getColorForTouchType(touchTypeName) : theme.palette.purple.main,
                      }}
                    >
                      <Icon htmlColor={theme.palette.white.main} />
                    </div>
                  </div>
                </TimelineDot>

                {/* Don't draw a connector on the last one */}
                {i !== touchCards.length - 1 && <TimelineConnector />}
              </TimelineSeparator>
              <TimelineContent className={classes.touchCardContainer}>
                <h3>
                  {touchTitle}
                  {touchTrigger.sendDateDelayed && <span className={classes.delayedTouchLabel}> (New Send Date)</span>}
                </h3>
                <h4>
                  {isExtraTouch
                    ? enumToReadableName(touchTrigger.touchType.name)
                    : getTouchDisplayName(touchTrigger)}
                  {isAdmin && ` (${touchTrigger._id})`}
                  {touchTrigger.customTouchTemplate && <span>{` (Customized ${touchTrigger.customTouchTemplate.type === 'BODY_TEXT' ? (touchTypeName !== ETouchType.Voicemail ? 'Text' : 'Email') : 'Design'})`}</span>}
                  {campaignStartDate ? ` - ${processed && !processingNotSentReason ? 'Sent' : 'Not Sent'}` : ''}
                  {isMailTouch && campaignStartDate && !processingNotSentReason ? ` - ${processed ? 'Mailed' : 'Mailing'} Out On ${moment(processAt).format('MMMM Do, YYYY')}` : ''}
                  {processingNotSentReason ? ` - ${getHumanProcessingNotSentReason(processingNotSentReason)}` : ''}
                </h4>

                <div className={classes.touchCardRow}>
                  <div className={classes.touchCardRowLeft}>
                    {leftContent}
                  </div>
                  <div className={classes.touchCardRowRight}>
                    {rightContent}
                    <div className={classes.actions}>
                      {/* Admins/brokerage admins can't edit touches */}
                      {!touchTrigger.processing && !touchTrigger.processed && isAnyAgent && (
                        <>
                          {isExtraTouch && (
                            <Tooltip
                              title="Delete"
                              placement="top"
                              classes={{ tooltip: classes.tooltip }}
                            >
                              <ButtonBase onClick={() => setIsDeletingId(touchTrigger._id)}>
                                <DeleteOutlineRoundedIcon htmlColor="#838391" />
                              </ButtonBase>
                            </Tooltip>
                          )}

                          {!isExtraTouch && !touchTrigger.sendDateDelayed && (
                            <Tooltip
                              title="Delay Send Date"
                              placement="top"
                              classes={{ tooltip: classes.tooltip }}
                            >
                              <ButtonBase onClick={() => setIsDelayingId(touchTrigger._id)}>
                                <UpdateIcon htmlColor="#838391" />
                              </ButtonBase>
                            </Tooltip>
                          )}

                          {isExtraTouch && (
                            <Tooltip
                              title="Reschedule"
                              placement="top"
                              classes={{ tooltip: classes.tooltip }}
                            >
                              <ButtonBase onClick={() => setIsReschedulingId(touchTrigger._id)}>
                                <UpdateIcon htmlColor="#838391" />
                              </ButtonBase>
                            </Tooltip>
                          )}

                          <Tooltip
                            title="Edit"
                            placement="top"
                            classes={{ tooltip: classes.tooltip }}
                          >
                            <ButtonBase onClick={() => setIsEditingId(touchTrigger._id)}>
                              <EditRoundedIcon htmlColor="#838391" />
                            </ButtonBase>
                          </Tooltip>
                        </>
                      )}

                      {/* Let admins preview emails by receiving an actual test email */}
                      {isAdmin && (touchTypeName === ETouchType.Email || hasVoicemailFallbackEmail(touchTrigger)) && (
                        <Tooltip
                          title="Receive Test Email"
                          placement="top"
                          classes={{ tooltip: classes.tooltip }}
                        >
                          <ButtonBase onClick={() => sendEmailPreview(touchTrigger)}><EmailIcon htmlColor="#838391" /></ButtonBase>
                        </Tooltip>
                      )}

                      {(touchTypeName !== ETouchType.Voicemail || hasVoicemailFallbackEmail(touchTrigger)) && (
                        <Tooltip
                          title={touchNeedsCustomization(touchTrigger)
                            ? 'Customize this touch to preview it'
                            : touchTypeName !== ETouchType.Voicemail ? 'Preview' : 'Preview Email'}
                          placement="top"
                          classes={{ tooltip: classes.tooltip }}
                        >
                          <ButtonBase onClick={() => handlePreviewTouch(touchTrigger)}><VisibilityRoundedIcon htmlColor="#838391" /></ButtonBase>
                        </Tooltip>
                      )}

                    </div>
                  </div>
                </div>
                <div className={classes.touchCardRowDivider} />

                {showInlineInsertion && (
                  <div className={classes.inlineInsertContainer}>
                    <Tooltip
                      title="Add extra touch here"
                      placement="top"
                      classes={{ tooltip: classes.tooltip }}
                    >
                      <ButtonBase
                        className={classes.inlineInsertButton}
                        onClick={() => openAddExtraTouchModal(
                          getDefaultExtraTouchSendAt(touchTrigger, nextTouchCard?.touchTrigger),
                        )}
                      >
                        <AddCircleOutlineIcon />
                      </ButtonBase>
                    </Tooltip>
                  </div>
                )}
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
    </>
  );
};

export default CampaignSteps;
